import { useEffect, useMemo, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { Modal, Row, Col } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import PratusSelect, {
  type OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import type { LayerWithNodeMeta } from 'hooks/internalLayer/types';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useUscgMapContext } from './UscgMapContext';
import { LayerFeaturesContent } from './LayerFeaturesContent';
import { InternalLayerVersionSelect } from './InternalLayerVersionSelect';
import { useAors } from 'hooks/useAors';
import { useUpdateInternalLayerMutation } from 'hooks/internalLayer/useUpdateInternalLayerMutation';
import { DraftNewVersionButton } from './DraftNewVersionButton';
import { NULL_VALUE } from 'constants/displayConstants';
import { useUpdateExternalLayerMutation } from 'hooks/externalLayer/useUpdateExternalLayerMutation';
import { useUpsertLayerNodeAssignmentMutation } from 'hooks/layerNode/useUpsertLayerNodeAssignmentMutation';
import { CategorySelect } from './CategorySelect';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { resolveLayerProvenance } from './utils/layerProvenance';

const DB_ROWS = [
  'Data Source',
  'Created',
  'Last Updated',
  'Region(s)',
  'Incident',
  'Type',
];

function getDbRowValue(label: string, layerWithType: LayerWithNodeMeta | null) {
  if (!layerWithType) return NULL_VALUE;

  const layer = layerWithType.layer;

  if (label === 'Data Source') {
    return layerWithType.type === 'internal'
      ? 'PRATUS'
      : resolveLayerProvenance(layerWithType.layer.serviceUrl).source;
  }
  if (label === 'Created') {
    return formatDateTime(layer.createdAt);
  }
  if (label === 'Last Updated') {
    return formatDateTime(layer.updatedAt);
  }
  if (label === 'Region(s)') {
    return layer.aorIds.length > 0 ? layer.aorIds.join(', ') : NULL_VALUE;
  }
  if (label === 'Incident') {
    return layer.incidentId ? layer.incidentId.toString() : NULL_VALUE;
  }
  if (label === 'Type') {
    return layerWithType.type === 'internal'
      ? 'Data layer (PRATUS)'
      : layerWithType.layer.itemType;
  }

  return NULL_VALUE;
}

type LayerDetailFormValues = {
  name: string;
  incidentId: string | null;
  aorIds: string[];
  defaultVisible: 'true' | 'false' | null;
};

const DEFAULT_VISIBLE_OPTIONS: OptionType[] = [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'False' },
];

export function LayerDetailModal({
  show,
  onClose,
  layerWithType,
}: {
  show: boolean;
  onClose: () => void;
  layerWithType: LayerWithNodeMeta | null;
}) {
  const aorText = useAorText();
  const groupGuid = useGroupGuid();
  const { data: incidents = [] } = useAllIncidents();
  const incidentOptions = useMemo<OptionType[]>(
    () =>
      incidents.map((incident) => ({
        value: incident.id,
        label: incident.name,
      })),
    [incidents]
  );
  const { data: aorsData } = useAors();
  const aorOptions = useMemo<OptionType[]>(
    () =>
      (aorsData ?? []).map((aor) => ({
        value: aor.id,
        label: aor.name,
      })),
    [aorsData]
  );

  const layerDetailForm = useForm<LayerDetailFormValues>({
    defaultValues: {
      name: layerWithType?.layer.name ?? '',
      incidentId: layerWithType?.layer.incidentId
        ? String(layerWithType.layer.incidentId)
        : null,
      aorIds: layerWithType?.layer.aorIds.map((aorId) => String(aorId)) ?? [],
      defaultVisible: layerWithType?.layer.defaultVisible ? 'true' : 'false',
    },
  });

  const { control, handleSubmit, reset } = layerDetailForm;
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    layerWithType?.node?.parentId ?? null
  );
  useEffect(() => {
    reset({
      name: layerWithType?.layer.name ?? '',
      incidentId: layerWithType?.layer.incidentId
        ? String(layerWithType.layer.incidentId)
        : null,
      aorIds: layerWithType?.layer.aorIds.map((aorId) => String(aorId)) ?? [],
      defaultVisible: layerWithType?.layer.defaultVisible ? 'true' : 'false',
    });
  }, [layerWithType, reset]);

  const { focusFeatureOnMap } = useUscgMapContext();
  const updateInternalLayer = useUpdateInternalLayerMutation();
  const updateExternalLayer = useUpdateExternalLayerMutation();
  const upsertLayerNodeAssignment = useUpsertLayerNodeAssignmentMutation();

  const onSubmit = handleSubmit(async (data) => {
    if (!layerWithType) return;

    const currentFolderId = layerWithType.node?.parentId ?? null;
    if (selectedFolderId !== currentFolderId) {
      await upsertLayerNodeAssignment.mutateAsync({
        kind: layerWithType.type,
        id: layerWithType.layer.id,
        groupGuid,
        targetFolderId: selectedFolderId,
      });
    }

    if (layerWithType?.type === 'external') {
      await updateExternalLayer.mutateAsync({
        externalLayerId: layerWithType.layer.id,
        name: data.name,
        incidentId: data.incidentId ?? null,
        aorIds: data.aorIds,
        defaultVisible:
          data.defaultVisible === 'true'
            ? true
            : data.defaultVisible === 'false'
              ? false
              : null,
      });
    } else if (layerWithType?.type === 'internal') {
      await updateInternalLayer.mutateAsync({
        internalLayerId: layerWithType.layer.id,
        name: data.name,
        incidentId: data.incidentId ?? null,
        aorIds: data.aorIds,
        defaultVisible:
          data.defaultVisible === 'true'
            ? true
            : data.defaultVisible === 'false'
              ? false
              : null,
      });
    }

    setSelectedFolderId(null);
    onClose();
  });

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <form onSubmit={onSubmit} data-tour="hub-cop-layer-detail">
        <Modal.Header closeButton closeVariant="white">
          <div className="tw:w-full tw:gap-2 tw:flex tw:items-center">
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <StylishNewInput
                  {...field}
                  aria-label="Layer name"
                  placeholder="Layer name"
                />
              )}
            />
            {layerWithType?.type === 'internal' && (
              <InternalLayerVersionSelect layer={layerWithType.layer} />
            )}
            {layerWithType?.type === 'internal' && (
              <DraftNewVersionButton internalLayer={layerWithType.layer} />
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="d-grid gap-3">
          <div className="d-grid" data-tour="hub-cop-layer-metadata">
            {DB_ROWS.map((label) => (
              <DetailRow
                key={label}
                label={label}
                value={getDbRowValue(label, layerWithType)}
              />
            ))}
            {layerWithType?.type === 'external' && (
              <>
                <DetailRow
                  label="Item ID"
                  value={layerWithType.layer.arcgisItemId}
                />
                <DetailRow
                  label="Service URL"
                  value={layerWithType.layer.serviceUrl}
                />
                <div data-tour="hub-cop-layer-restrictions">
                  <DetailRow
                    label="Auth Type"
                    value={layerWithType.layer.authType}
                  />
                </div>
              </>
            )}
          </div>
          <Row>
            <Col>
              <CategorySelect
                label="Category"
                value={selectedFolderId}
                onChange={setSelectedFolderId}
              />
            </Col>
            <Col data-tour="hub-cop-layer-incident">
              <Controller
                control={control}
                name="incidentId"
                render={({ field: { value, onChange } }) => {
                  const selectedIncidentOption =
                    incidentOptions.find(
                      (option) => String(option.value) === value
                    ) ?? null;

                  return (
                    <PratusSelect
                      label="Incident"
                      isClearable
                      options={incidentOptions}
                      value={selectedIncidentOption ?? undefined}
                      onChange={(val) => {
                        if (!val) {
                          onChange(null);
                          return;
                        }

                        const option = val as OptionType;
                        onChange(String(option.value));
                      }}
                    />
                  );
                }}
              />
            </Col>
            <Col>
              <Controller
                control={control}
                name="aorIds"
                render={({ field: { value, onChange } }) => {
                  const selectedOptions = aorOptions.filter((option) =>
                    (value ?? []).includes(String(option.value))
                  );

                  return (
                    <PratusSelect
                      label={aorText('AOR')}
                      isMulti
                      options={aorOptions}
                      value={selectedOptions}
                      onChange={(val) => {
                        if (!val || !Array.isArray(val)) {
                          onChange([]);
                          return;
                        }

                        const options = val as OptionType[];
                        onChange(options.map((option) => String(option.value)));
                      }}
                    />
                  );
                }}
              />
            </Col>
            <Col>
              <Controller
                control={control}
                name="defaultVisible"
                render={({ field: { value, onChange } }) => {
                  const selectedDefaultVisibleOption =
                    DEFAULT_VISIBLE_OPTIONS.find((opt) => opt.value === value);

                  return (
                    <PratusSelect
                      isClearable
                      label="Default visible"
                      options={DEFAULT_VISIBLE_OPTIONS}
                      value={selectedDefaultVisibleOption}
                      onChange={(val) => {
                        if (!val || Array.isArray(val)) {
                          onChange(null);
                          return;
                        }

                        const option = val as OptionType;
                        onChange(option.value);
                      }}
                    />
                  );
                }}
              />
            </Col>
          </Row>

          {layerWithType && (
            <div className="mt-2">
              <LayerFeaturesContent
                layer={layerWithType.layer}
                layerType={layerWithType.type}
                onZoomToExternalGraphic={(graphic) => {
                  void focusFeatureOnMap({
                    graphic,
                    layer: {
                      kind: 'external',
                      layerId: String(layerWithType.layer.id),
                    },
                  });
                }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <StylishNewButton
            type="button"
            className="button--secondary"
            onClick={onClose}
          >
            Cancel
          </StylishNewButton>

          <StylishNewButton
            type="submit"
            className="button--primary"
            disabled={
              updateInternalLayer.isPending ||
              updateExternalLayer.isPending ||
              upsertLayerNodeAssignment.isPending
            }
          >
            Save
          </StylishNewButton>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="d-flex justify-content-between align-items-center border-bottom small gap-2 border-neutral-500 py-2">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
